import { useEffect, useState } from "react";
import type { PropsWithChildren } from "react";
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from "framer-motion";
import { useTranslation } from "react-i18next";

import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { populationsApi, type AudienceSummary } from "@/lib/api";
import type { TurnSuggestions } from "@digital-twins/shared-types/assistant";

import { SuggestionBanner } from "./SuggestionBanners";

interface SuggestionSlotProps {
  threadToken: string;
  turnKey: string;
  pending: boolean;
  /** False while the latest answer is still streaming or being written out. */
  revealed?: boolean;
  suggestions: TurnSuggestions["suggestions"];
  onSelect: (suggestion: TurnSuggestions["suggestions"][number]) => void;
  onCountChange?: (count: number) => void;
  selecting?: boolean;
}

function dismissalStorageKey(threadToken: string, turnKey: string): string {
  return `assistant-suggestion-dismissals:v2:${threadToken}:${turnKey}`;
}

function readDismissals(key: string): Array<string> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(value)
      ? value.filter(
          (identity): identity is string => typeof identity === "string",
        )
      : [];
  } catch {
    return [];
  }
}

/**
 * D7: re-key a turn's dismissals from its temporary per-turn key to the
 * assistant message token once `done` names it, so they survive a reload.
 * Merges into whatever the target key already holds and removes the source.
 * Idempotent; storage failures are ignored like every other dismissal write.
 */
export function moveSuggestionDismissals(
  threadToken: string,
  fromKey: string,
  toKey: string,
): void {
  if (!fromKey || !toKey || fromKey === toKey) {
    return;
  }
  const fromStorageKey = dismissalStorageKey(threadToken, fromKey);
  const toStorageKey = dismissalStorageKey(threadToken, toKey);
  const moved = readDismissals(fromStorageKey);
  if (moved.length === 0) {
    return;
  }
  const merged = [...new Set([...readDismissals(toStorageKey), ...moved])];
  try {
    localStorage.setItem(toStorageKey, JSON.stringify(merged));
    localStorage.removeItem(fromStorageKey);
  } catch {
    /* Storage can be disabled. */
  }
}

/** Exact structural identity; rank is not the card the user dismissed. */
function suggestionIdentity(
  suggestion: TurnSuggestions["suggestions"][number],
): string {
  return JSON.stringify(suggestion, (_key, value) => {
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      return Object.fromEntries(
        Object.keys(value)
          .sort()
          .map((key) => [key, value[key]]),
      );
    }
    return value;
  });
}

/** Outgoing cards stay visible during the fade, but cannot be launched twice. */
function SuggestionCards({ children }: PropsWithChildren) {
  const present = useIsPresent();
  const reducedMotion = useReducedMotion();
  return (
    <motion.fieldset
      className="col-start-1 row-start-1 m-0 flex min-w-0 flex-col gap-3 border-0 p-0"
      disabled={!present}
      aria-hidden={!present}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.15, ease: "easeOut" }}
    >
      {children}
    </motion.fieldset>
  );
}

/** The slot is reserved at turn start. Only an empty, finished turn collapses it. */
export function SuggestionSlot(props: SuggestionSlotProps) {
  const {
    threadToken,
    turnKey,
    pending,
    revealed = true,
    suggestions,
    onSelect,
    onCountChange,
    selecting = false,
  } = props;
  const { t } = useTranslation("app");
  const storageKey = dismissalStorageKey(threadToken, turnKey);
  const [expandedTurn, setExpandedTurn] = useState<string | null>(null);
  const expanded = expandedTurn === storageKey;
  const [dismissals, setDismissals] = useState(() => ({
    key: storageKey,
    identities: readDismissals(storageKey),
  }));
  const dismissed =
    dismissals.key === storageKey
      ? dismissals.identities
      : readDismissals(storageKey);
  const count = suggestions.filter(
    (suggestion) => !dismissed.includes(suggestionIdentity(suggestion)),
  ).length;

  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  const audienceNames = useLabelLookup(suggestions);

  function dismiss(identity: string) {
    const identities = [...dismissed, identity];
    setDismissals({ key: storageKey, identities });
    try {
      localStorage.setItem(storageKey, JSON.stringify(identities));
    } catch {
      /* Storage can be disabled. */
    }
  }

  /* There is nothing to show: no reserved skeleton, no empty grey box, in
   * either the pending or finished state. */
  if (count === 0 || !revealed) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative left-1/2 flex min-w-0 shrink-0 -translate-x-1/2 flex-col gap-2",
        count === 2 ? "w-full max-w-3xl" : "w-[min(100cqw,72rem)]",
      )}
      aria-label={t("threads.sessions.suggestions")}
      aria-busy={selecting || undefined}
      data-testid="thread-suggestion-slot"
    >
      <div className="flex items-baseline justify-between gap-3">
        <Text size="sm" color="muted">
          {t("threads.sessions.suggestions")}
        </Text>
        {selecting ? (
          <Text
            size="xs"
            color="muted"
            role="status"
            data-testid="thread-suggestion-selecting"
          >
            {t("threads.sessions.picking")}
          </Text>
        ) : null}
      </div>
      <div className="grid min-w-0 max-w-full">
        <AnimatePresence initial={false}>
          <SuggestionCards key={JSON.stringify(suggestions)}>
            <div
              className="grid min-w-0 max-w-full auto-cols-[minmax(min(20rem,100%),min(23rem,100%))] grid-flow-col grid-rows-[repeat(5,auto)] snap-x snap-proximity items-stretch gap-3 overflow-x-auto overscroll-x-contain scroll-px-1 p-1"
              role="group"
              aria-label={t("threads.sessions.suggestions")}
              data-testid="thread-suggestion-row"
            >
              {suggestions.map((suggestion, index) =>
                dismissed.includes(suggestionIdentity(suggestion)) ? null : (
                  <SuggestionBanner
                    key={`${turnKey}:${index}`}
                    suggestion={suggestion}
                    index={index}
                    audienceName={
                      suggestion.kind === "image"
                        ? undefined
                        : audienceNames[
                            suggestion.kind === "room"
                              ? suggestion.spec.twins.audienceToken
                              : suggestion.spec.audience.audienceToken
                          ]?.name
                    }
                    audience={
                      suggestion.kind === "image"
                        ? undefined
                        : audienceNames[
                            suggestion.kind === "room"
                              ? suggestion.spec.twins.audienceToken
                              : suggestion.spec.audience.audienceToken
                          ]?.audience
                    }
                    disabled={selecting}
                    expanded={expanded}
                    onToggleDetails={() =>
                      setExpandedTurn(expanded ? null : storageKey)
                    }
                    onSelect={() => onSelect(suggestion)}
                    onDismiss={() => dismiss(suggestionIdentity(suggestion))}
                  />
                ),
              )}
            </div>
          </SuggestionCards>
        </AnimatePresence>
      </div>
    </section>
  );
}

interface SuggestionLabel {
  name: string;
  audience?: AudienceSummary;
}

function useLabelLookup(
  suggestions: TurnSuggestions["suggestions"],
): Record<string, SuggestionLabel> {
  const key = lookupKey(suggestions);
  const [names, setNames] = useState<Record<string, SuggestionLabel>>({});
  useEffect(() => {
    if (!key) {
      return;
    }
    let cancelled = false;
    const tokens = key.split("\n");
    void (async () => {
      const next: Record<string, SuggestionLabel> = {};
      await Promise.all(
        tokens.map(async (token) => {
          try {
            const audience = await populationsApi.get(token);
            if (audience?.name) {
              next[token] = { name: audience.name, audience };
            }
          } catch {
            /* A missing audience leaves the tag off. */
          }
        }),
      );
      if (!cancelled) {
        setNames(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);
  return names;
}

function lookupKey(suggestions: TurnSuggestions["suggestions"]): string {
  const tokens = new Set<string>();
  for (const suggestion of suggestions) {
    if (suggestion.kind === "image") continue;
    tokens.add(
      suggestion.kind === "room"
        ? suggestion.spec.twins.audienceToken
        : suggestion.spec.audience.audienceToken,
    );
  }
  return Array.from(tokens).sort().join("\n");
}
